import { useEffect, useState, useMemo } from "react";
import { Navigate, Link, useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { useAuth } from "@/contexts/AuthContext";
import { http } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowRight, Award, BookOpen, Headphones, Image as ImageIcon, FileText,
  Archive, Star, MessageCircle, Crown, User as UserIcon, ShoppingBag, Lock,
} from "lucide-react";
import MyTruths from "@/components/MyTruths";
import {
  WeeklyReleases, AudioLibrary, WallpapersGrid, Printables, TruthArchive,
  ExclusiveReleases, CommunityPolls, TruthSuggestions, LiveQA,
} from "@/components/MemberContent";

// Sidebar sections. `required` is the minimum tier_rank to unlock.
// Section keys are intentionally generic so Phase B can drop content into them.
const SECTIONS = [
  { key: "overview",  label: "Overview",         icon: UserIcon,      required: 0 },
  { key: "truths",    label: "Your Truths",      icon: BookOpen,      required: 0 },
  { key: "weekly",    label: "Weekly Releases",  icon: BookOpen,      required: 1 },
  { key: "audio",     label: "Audio Library",    icon: Headphones,    required: 1 },
  { key: "wallpapers",label: "Wallpapers",        icon: ImageIcon,    required: 1 },
  { key: "printables",label: "Printables",        icon: FileText,     required: 1 },
  { key: "archive",   label: "Truth Archive",    icon: Archive,       required: 2 },
  { key: "exclusive", label: "Exclusive Releases", icon: Star,        required: 2 },
  { key: "community", label: "Community",        icon: MessageCircle, required: 2 },
  { key: "liveqa",    label: "Live Q&A",         icon: MessageCircle, required: 3 },
  { key: "suggest",   label: "Suggest A Truth",  icon: Star,          required: 3 },
  { key: "founding",  label: "Founding Wall",    icon: Crown,         required: 3 },
  { key: "orders",    label: "Orders",           icon: ShoppingBag,   required: 0 },
  { key: "account",   label: "Account & Billing",icon: UserIcon,      required: 0 },
];

const TIER_LABEL = {
  truth_keeper: "Truth Keeper",
  truth_seeker: "Truth Seeker",
  truth_maker:  "Truth Maker",
};

export default function Account() {
  const { user, loading } = useAuth();
  const [search, setSearch] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [subs, setSubs] = useState([]);
  const [tier, setTier] = useState({ tier_slug: null, tier_rank: 0, features: [], is_admin: false });
  const [foundingWall, setFoundingWall] = useState([]);
  const active = search.get("tab") || "overview";

  useEffect(() => {
    if (!user) return;
    http.get("/orders/me").then(({ data }) => setOrders(data)).catch(() => {});
    http.get("/subscriptions/me").then(({ data }) => setSubs(data)).catch(() => {});
    http.get("/me/tier").then(({ data }) => setTier(data)).catch(() => {});
    http.get("/me/founding-wall").then(({ data }) => setFoundingWall(data.members || [])).catch(() => {});
  }, [user]);

  const setTab = (key) => { search.set("tab", key); setSearch(search, { replace: true }); };

  const cancelSub = async (sid) => {
    if (!window.confirm("Cancel this subscription?")) return;
    try {
      await http.post(`/subscriptions/${sid}/cancel`);
      toast.success("Canceled.");
      setSubs((s) => s.map((x) => (x.id === sid ? { ...x, status: "canceled" } : x)));
    } catch { toast.error("Could not cancel."); }
  };

  const tierRank = tier.tier_rank || (tier.is_admin ? 3 : 0);
  const downloads = useMemo(() => orders.flatMap((o) =>
    (o.items || []).filter((i) => i.is_digital && o.payment_status === "paid")
      .map((i) => ({ ...i, order_number: o.order_number, created_at: o.created_at }))
  ), [orders]);

  if (loading) return <Layout><div className="py-32 text-center font-ui text-[#8a857d]">Loading…</div></Layout>;
  if (!user) return <Navigate to="/login" state={{ next: "/account" }} replace />;

  const renderSection = () => {
    const isMaker = tierRank >= 3;
    const isSeeker = tierRank >= 2;
    switch (active) {
      case "overview":    return <OverviewSection user={user} tier={tier} subs={subs} orders={orders} />;
      case "truths":      return <div className="border border-white/10 p-6 bg-[#0a0a0a]"><MyTruths /></div>;
      case "weekly":      return tierRank >= 1 ? <WeeklyReleases/> : <UpgradeGate label="Weekly Releases" need="Truth Keeper"/>;
      case "audio":       return tierRank >= 1 ? <AudioLibrary/> : <UpgradeGate label="Audio Library" need="Truth Keeper"/>;
      case "wallpapers":  return tierRank >= 1 ? <WallpapersGrid/> : <UpgradeGate label="Wallpapers" need="Truth Keeper"/>;
      case "printables":  return tierRank >= 1 ? <Printables/> : <UpgradeGate label="Printables" need="Truth Keeper"/>;
      case "archive":     return isSeeker ? <TruthArchive/> : <UpgradeGate label="Truth Archive" need="Truth Seeker"/>;
      case "exclusive":   return isSeeker ? <ExclusiveReleases/> : <UpgradeGate label="Exclusive Releases" need="Truth Seeker"/>;
      case "community":   return isSeeker ? <CommunityPolls/> : <UpgradeGate label="Community Polls" need="Truth Seeker"/>;
      case "liveqa":      return isMaker ? <LiveQA canView={true}/> : <UpgradeGate label="Live Q&A" need="Truth Maker"/>;
      case "suggest":     return isMaker ? <TruthSuggestions canSubmit={true}/> : <UpgradeGate label="Suggest A Truth" need="Truth Maker"/>;
      case "founding":    return <FoundingWallSection members={foundingWall} userTier={tier.tier_slug} />;
      case "orders":      return <OrdersSection orders={orders} downloads={downloads} />;
      case "account":     return <AccountSection user={user} subs={subs} cancelSub={cancelSub} />;
      default:            return null;
    }
  };

  return (
    <Layout>
      <section className="bg-[#050505] pt-12 pb-20">
        <div className="max-w-[1480px] mx-auto px-4 lg:px-10">
          {/* Welcome banner */}
          <div className="mb-10" data-testid="dashboard-welcome">
            <div className="font-ui text-[11px] tracking-[0.32em] text-[#C9A055]">MEMBER DASHBOARD</div>
            <h1 className="font-display text-4xl md:text-5xl text-[#F4F1EA] mt-3">
              Welcome back, <span className="text-[#C9A055]">{(user.name || "Truth Seeker").split(" ")[0]}.</span>
            </h1>
            <p className="font-script text-[#C9A055] text-xl md:text-2xl mt-3">Every truth starts with something ordinary.</p>
            <p className="font-body text-[#B5B0A7] mt-2 max-w-2xl">A tree. A hat. A door. A match. <span className="text-[#F4F1EA]">The lesson is what changes everything.</span></p>
            {tier.tier_slug && (
              <div className="inline-flex items-center gap-2 mt-5 px-4 py-2 border border-[#C9A055] bg-[#C9A055]/10" data-testid="tier-badge">
                <Crown size={14} className="text-[#C9A055]"/>
                <span className="font-ui text-[11px] tracking-[0.32em] text-[#C9A055] uppercase">{TIER_LABEL[tier.tier_slug]}</span>
              </div>
            )}
            {!tier.tier_slug && !tier.is_admin && (
              <div className="mt-6">
                <Link to="/subscriptions" className="btn-gold inline-flex items-center gap-2" data-testid="join-club-cta">
                  Join The Club <ArrowRight size={14}/>
                </Link>
              </div>
            )}
          </div>

          <div className="grid lg:grid-cols-[260px_1fr] gap-8">
            {/* Mobile section picker — replaces sidebar on small screens so the content
                doesn't get pushed below 14 stacked buttons. */}
            <div className="lg:hidden" data-testid="dashboard-nav-mobile">
              <label className="block">
                <span className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055] uppercase">Section</span>
                <select
                  value={active}
                  onChange={(e) => setTab(e.target.value)}
                  data-testid="dashboard-nav-select"
                  className="mt-2 w-full bg-[#0a0a0a] border border-[#C9A055]/40 text-[#F4F1EA] font-ui text-[12px] tracking-[0.22em] uppercase px-4 py-3 focus:border-[#C9A055] focus:outline-none"
                >
                  {SECTIONS.map((s) => {
                    const locked = s.required > tierRank;
                    return (
                      <option key={s.key} value={s.key}>
                        {s.label}{locked ? " · 🔒" : ""}
                      </option>
                    );
                  })}
                </select>
              </label>
            </div>

            {/* Desktop sidebar (lg and up) */}
            <aside className="hidden lg:block border border-white/10 bg-[#0a0a0a] p-3 self-start sticky top-[110px]" data-testid="dashboard-nav">
              {SECTIONS.map((s) => {
                const Icon = s.icon;
                const locked = s.required > tierRank;
                return (
                  <button
                    key={s.key}
                    data-testid={`tab-${s.key}`}
                    onClick={() => setTab(s.key)}
                    className={`w-full text-left flex items-center gap-3 px-3 py-2.5 transition-colors ${active === s.key ? "bg-[#C9A055]/10 border-l-2 border-[#C9A055] text-[#C9A055]" : "border-l-2 border-transparent text-[#B5B0A7] hover:text-[#F4F1EA]"}`}
                  >
                    <Icon size={14}/>
                    <span className="font-ui text-[11px] tracking-[0.22em] uppercase flex-1">{s.label}</span>
                    {locked && <Lock size={11} className="text-[#5a5650]"/>}
                  </button>
                );
              })}
            </aside>

            {/* Main panel */}
            <main className="space-y-6" data-testid={`dashboard-panel-${active}`}>
              {renderSection()}
            </main>
          </div>
        </div>
      </section>
    </Layout>
  );
}

// ===================== Section components =====================

function OverviewSection({ user, tier, subs, orders }) {
  return (
    <>
      <div className="grid md:grid-cols-3 gap-4">
        <StatCard label="Profile" value={user.name} sub={user.email}/>
        <StatCard label="Referral Credit" value={`$${(user.referral_credit || 0).toFixed(2)}`} sub={`Code · ${user.referral_code}`} accent/>
        <StatCard label="Gift Card Balance" value={`$${(user.gift_card_balance || 0).toFixed(2)}`} sub="Available at checkout" accent/>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="border border-white/10 bg-[#0a0a0a] p-6">
          <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055] mb-3">YOUR MEMBERSHIP</div>
          {tier.tier_slug ? (
            <>
              <div className="font-display text-3xl text-[#F4F1EA]">{TIER_LABEL[tier.tier_slug]?.toUpperCase()}</div>
              <div className="font-ui text-[10px] tracking-[0.32em] text-[#5fc377] mt-1">ACTIVE</div>
              <div className="font-body text-[12px] text-[#8a857d] mt-3">{tier.features.length} features unlocked.</div>
            </>
          ) : (
            <>
              <div className="font-display text-3xl text-[#F4F1EA]">Not a member yet.</div>
              <p className="font-body text-[13px] text-[#B5B0A7] mt-3">Pick a tier and start receiving your weekly truth.</p>
              <Link to="/subscriptions" className="btn-gold mt-4 inline-flex items-center gap-2">See Tiers <ArrowRight size={12}/></Link>
            </>
          )}
        </div>
        <div className="border border-white/10 bg-[#0a0a0a] p-6">
          <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055] mb-3">QUICK STATS</div>
          <div className="grid grid-cols-2 gap-4">
            <Stat label="Orders" value={orders.length}/>
            <Stat label="Active Subs" value={subs.filter((s) => s.status === "active").length}/>
          </div>
        </div>
      </div>
    </>
  );
}

function StatCard({ label, value, sub, accent }) {
  return (
    <div className={`border p-5 ${accent ? "border-[#C9A055]/30 bg-gradient-to-br from-[#1a1814] to-[#0a0a0a]" : "border-white/10 bg-[#0a0a0a]"}`}>
      <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055] mb-2">{label.toUpperCase()}</div>
      <div className="font-display text-2xl text-[#F4F1EA] truncate">{value}</div>
      {sub && <div className="font-ui text-[11px] text-[#8a857d] mt-1 truncate">{sub}</div>}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="font-display text-3xl text-[#C9A055]">{value}</div>
      <div className="font-ui text-[10px] tracking-[0.22em] text-[#8a857d] mt-1">{label.toUpperCase()}</div>
    </div>
  );
}

function ComingSoon({ title, hint }) {
  return (
    <div className="border border-white/10 bg-[#0a0a0a] p-10 text-center">
      <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055]">COMING SOON</div>
      <h2 className="font-display text-3xl text-[#F4F1EA] mt-3">{title}</h2>
      <p className="font-body text-[#B5B0A7] max-w-md mx-auto mt-4 leading-relaxed">{hint}</p>
      <p className="font-script text-[#C9A055] text-lg mt-6">Phase B is on its way.</p>
    </div>
  );
}

function UpgradeGate({ label, need }) {
  return (
    <div className="border border-[#C9A055]/30 bg-gradient-to-br from-[#1a1814] to-[#0a0a0a] p-10 text-center" data-testid="upgrade-gate">
      <Lock className="text-[#C9A055] mx-auto" size={28}/>
      <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055] mt-4">{need.toUpperCase()} REQUIRED</div>
      <h2 className="font-display text-3xl text-[#F4F1EA] mt-2">{label}</h2>
      <p className="font-body text-[#B5B0A7] max-w-md mx-auto mt-4 leading-relaxed">
        This section unlocks at the <strong className="text-[#C9A055]">{need}</strong> tier and above.
      </p>
      <Link to="/subscriptions" className="btn-gold inline-flex items-center gap-2 mt-6"><ArrowRight size={14}/> Upgrade Now</Link>
    </div>
  );
}

function FoundingWallSection({ members, userTier }) {
  const visible = userTier === "truth_maker";
  return (
    <div className="border border-[#C9A055]/30 bg-gradient-to-br from-[#1a1814] to-[#0a0a0a] p-10">
      <Crown className="text-[#C9A055] mx-auto" size={32}/>
      <div className="text-center mt-4">
        <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055]">FOUNDING WALL</div>
        <h2 className="font-display text-4xl text-[#F4F1EA] mt-2">Names Behind The Movement</h2>
        <p className="font-body text-[#B5B0A7] max-w-xl mx-auto mt-4">The Truth Makers who believed from the very beginning. {members.length} {members.length === 1 ? "name" : "names"} so far.</p>
      </div>
      {members.length === 0 ? (
        <p className="text-center font-body italic text-[#8a857d] mt-10">No Truth Makers yet. Be the first to leave your name on the wall.</p>
      ) : (
        <div className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="founding-wall-members">
          {members.map((m, i) => (
            <div key={i} className="border border-[#C9A055]/20 bg-black p-4 text-center">
              <div className="font-display text-lg text-[#F4F1EA]">{visible ? m.name : (m.name || "").split(" ")[0]}</div>
              {visible && (m.city || m.state) && (
                <div className="font-ui text-[10px] tracking-[0.22em] text-[#8a857d] mt-1">{[m.city, m.state].filter(Boolean).join(", ")}</div>
              )}
            </div>
          ))}
        </div>
      )}
      {userTier !== "truth_maker" && (
        <div className="text-center mt-10 border-t border-white/10 pt-8">
          <p className="font-body text-[#B5B0A7]">Become a Truth Maker to see the full wall and add your own name.</p>
          <Link to="/subscriptions" className="btn-gold inline-flex items-center gap-2 mt-4">Become a Truth Maker <ArrowRight size={14}/></Link>
        </div>
      )}
    </div>
  );
}

function OrdersSection({ orders, downloads }) {
  return (
    <>
      <div className="border border-white/10 bg-[#0a0a0a] p-6">
        <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055] mb-4">ORDERS</div>
        {orders.length === 0 ? (
          <p className="font-body italic text-[#B5B0A7]">No orders yet. <Link to="/shop" className="text-[#C9A055]">Find your first truth →</Link></p>
        ) : (
          <div className="space-y-2" data-testid="account-orders">
            {orders.map((o) => (
              <Link key={o.id} to={`/proof/${o.order_number}`} className="block border border-white/10 hover:border-[#C9A055]/50 p-4 transition-colors">
                <div className="flex flex-wrap justify-between items-center gap-2">
                  <div>
                    <div className="font-ui-tight text-sm text-[#F4F1EA]">{o.order_number}</div>
                    <div className="font-body text-sm text-[#B5B0A7]">{(o.items || []).length} item(s) · {o.payment_method}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-ui-tight text-[#C9A055]">${(o.total || 0).toFixed(2)}</div>
                    <div className={`font-ui text-[10px] tracking-[0.22em] uppercase ${o.payment_status === "paid" ? "text-emerald-400" : "text-amber-400"}`}>{o.payment_status}</div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
      {downloads.length > 0 && (
        <div className="border border-white/10 bg-[#0a0a0a] p-6">
          <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055] mb-4">DIGITAL DOWNLOADS</div>
          <div className="space-y-2">
            {downloads.map((d, i) => (
              <div key={i} className="flex items-center justify-between border border-white/10 p-3">
                <div>
                  <div className="font-ui-tight text-sm text-[#F4F1EA]">{d.title}</div>
                  <div className="font-ui text-xs text-[#8a857d]">Order {d.order_number}</div>
                </div>
                <a href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/orders/by-number/${d.order_number}/download/${d.artwork_id}`} target="_blank" rel="noreferrer" className="btn-ghost" data-testid={`account-download-${d.artwork_id}`}>Download →</a>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

function AccountSection({ user, subs, cancelSub }) {
  return (
    <div className="space-y-4">
      <div className="border border-white/10 bg-[#0a0a0a] p-6">
        <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055] mb-3">PROFILE</div>
        <div className="grid sm:grid-cols-2 gap-3 font-body text-[14px] text-[#F4F1EA]">
          <div><div className="text-[#8a857d] text-[11px] tracking-[0.22em] uppercase">Name</div>{user.name}</div>
          <div><div className="text-[#8a857d] text-[11px] tracking-[0.22em] uppercase">Email</div>{user.email}</div>
          <div><div className="text-[#8a857d] text-[11px] tracking-[0.22em] uppercase">Referral Code</div><span className="font-mono">{user.referral_code}</span></div>
          <div><div className="text-[#8a857d] text-[11px] tracking-[0.22em] uppercase">Member Since</div>{user.created_at ? new Date(user.created_at).toLocaleDateString() : "—"}</div>
        </div>
      </div>
      <div className="border border-white/10 bg-[#0a0a0a] p-6">
        <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055] mb-3">SUBSCRIPTIONS</div>
        {subs.length === 0 ? (
          <>
            <p className="font-body italic text-[#B5B0A7] mb-3">No subscriptions yet.</p>
            <Link to="/subscriptions" className="btn-outline text-[10px]">Join the Club <ArrowRight size={12}/></Link>
          </>
        ) : subs.map((s) => (
          <div key={s.id} className="border border-white/10 p-4 mb-2 flex flex-wrap items-center justify-between gap-3" data-testid={`sub-row-${s.id}`}>
            <div>
              <div className="font-display text-xl text-[#F4F1EA]">{s.plan?.name || "Plan"}</div>
              <div className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#8a857d]">${(s.price || 0).toFixed(2)} / {s.frequency || s.billing_cycle || "month"} · <span className={s.status === "active" ? "text-emerald-400" : "text-amber-400"}>{s.status}</span></div>
            </div>
            {s.status === "active" && (
              <button onClick={() => cancelSub(s.id)} className="btn-ghost text-red-400 text-[10px]" data-testid={`cancel-sub-${s.id}`}>Cancel</button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
